import { countPopulatedVitals } from './jsonValidator';
import { getHandoverPatients } from './handoverUtils';

/** Filler/greeting tokens only — used to detect accidental mic taps, not clinical classification. */
const GREETING_WORDS = new Set([
  'hello',
  'hi',
  'hey',
  'he',
  'test',
  'testing',
  'uh',
  'um',
  'ahlan',
  'marhaba',
  'salam',
  'yes',
  'no',
  'ok',
  'okay',
]);

const META_PLACEHOLDER_PATTERNS = [
  /^not\s+documented\.?$/i,
  /insufficient clinical information/i,
  /no patient-reported symptoms/i,
  /no objective assessment/i,
  /not documented in the transcript/i,
  /not provided in the transcript/i,
  /obtain patient identification/i,
  /complete nursing assessment/i,
  /no clinical content/i,
  /unable to formulate/i,
  /document vital signs and current complaints if any/i,
  /no nursing documentation/i,
  /unrelated to patient care/i,
];

function tokenizeTranscript(transcript) {
  return (transcript.match(/[\u0600-\u06FF]+|[a-z']+/gi) || []).map((word) =>
    word.toLowerCase().replace(/[.,!?]/g, '')
  );
}

function meaningfulWords(transcript) {
  return tokenizeTranscript(transcript).filter((word) => word && !GREETING_WORDS.has(word));
}

/**
 * Lightweight pre-LLM gate for empty/noise transcripts only.
 * Clinical note-type detection and medication extraction are handled by the LLM
 * (detectNoteTypes / generateNote) — not hardcoded word lists here.
 */
export function isLikelyNonClinicalTranscript(transcript) {
  const raw = transcript?.trim();
  if (!raw) return true;

  if (isMetaPlaceholderText(raw)) return true;

  const tokens = meaningfulWords(raw);
  if (!tokens.length) return true;

  const hasDigit = /\d/.test(raw);
  const hasArabic = /[\u0600-\u06FF]{2,}/.test(raw);

  // Block only very short accidental taps with no numbers and no Arabic speech.
  // Also block longer greeting/filler-only phrases STT sometimes invents on silence.
  if (tokens.length <= 3 && !hasDigit && !hasArabic) {
    return true;
  }

  return false;
}

/** True if any transcript variant has enough substance to send to the LLM. */
export function transcriptHasClinicalSignal(...variants) {
  return variants.some((text) => text?.trim() && !isLikelyNonClinicalTranscript(text));
}

export function isMetaPlaceholderText(text) {
  const value = String(text || '').trim();
  if (!value) return true;
  return META_PLACEHOLDER_PATTERNS.some((pattern) => pattern.test(value));
}

function hasSubstantiveSoap(soap = {}) {
  const sections = [soap.subjective, soap.objective, soap.assessment, soap.plan];
  return sections.some((section) => !isMetaPlaceholderText(section));
}

function hasSubstantiveSbar(sbar = {}) {
  const sections = [sbar.situation, sbar.background, sbar.assessment, sbar.recommendation];
  return sections.some((section) => !isMetaPlaceholderText(section));
}

export function hasDocumentableClinicalContent(note, type) {
  if (!note) return false;
  if (note.clinical_content === false) return false;

  const hasNoClinicalFlag = (note.flags || []).some((flag) => {
    const message = typeof flag === 'string' ? flag : flag?.message || '';
    return message.toLowerCase().includes('no clinical content');
  });
  if (hasNoClinicalFlag) return false;

  switch (type) {
    case 'progress_note': {
      const hasVitals =
        countPopulatedVitals({ vitals_extracted: note.vitals_extracted }) > 0 ||
        countPopulatedVitals(note) > 0;
      const hasNarrative = typeof note.narrative === 'string' && note.narrative.trim()
        && !isMetaPlaceholderText(note.narrative);
      return hasNarrative || hasSubstantiveSoap(note.soap) || hasVitals;
    }
    case 'vitals':
      return countPopulatedVitals(note) >= 1;
    case 'medication_admin':
      return (note.medications_administered?.length || note.medications?.length || 0) > 0;
    case 'fluid_balance':
      return (note.fluid_inputs?.length || 0) + (note.fluid_outputs?.length || 0) > 0;
    case 'handover': {
      const narrative =
        typeof note.narrative === 'string' && note.narrative.trim()
        && !isMetaPlaceholderText(note.narrative);
      if (narrative) return true;
      return getHandoverPatients(note).some(
        (entry) =>
          (typeof entry.narrative === 'string'
            && entry.narrative.trim()
            && !isMetaPlaceholderText(entry.narrative))
          || hasSubstantiveSbar(entry.sbar)
          || (entry.outstanding_tasks?.length || 0) > 0
      );
    }
    case 'admission':
      return Boolean(
        note.chief_complaint?.trim() ||
          note.hpi?.trim() ||
          note.allergies?.length ||
          note.home_medications?.length
      );
    case 'discharge':
      return Boolean(
        (typeof note.narrative === 'string' && note.narrative.trim()
          && !isMetaPlaceholderText(note.narrative))
          || note.condition_at_discharge?.trim()
          || note.discharge_medications?.length
          || note.education?.length
          || note.special_instructions?.trim()
          || note.discharge_mode?.trim()
      );
    case 'care_plan': {
      const plan = note.care_plan || {};
      const hasDiagnosis = !isMetaPlaceholderText(plan.nursing_diagnosis);
      const hasGoals = !isMetaPlaceholderText(plan.goals);
      const hasInterventions = (plan.interventions || []).some(
        (item) => !isMetaPlaceholderText(item)
      );
      const hasEvaluation = !isMetaPlaceholderText(plan.evaluation);
      return hasDiagnosis || hasGoals || hasInterventions || hasEvaluation;
    }
    default:
      return true;
  }
}
